import React, { useState, useEffect, useMemo } from 'react';
import {
  Paper,
  Typography,
  Box,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@mui/material';
import { LocalShipping } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';


interface Facility {
  id: number;
  name: string;
}

interface Trailer {
  uuid: string;
  trailer_number: string;
  carrier?: { name: string };
  status?: { name: string };
}

interface EmptyTrailersByScacWidgetProps {
  selectedFacility: Facility | null;
  compactMode: boolean;
  data?: Record<string, any> | null;
  isRefreshing?: boolean;
  onRefresh?: () => void;
}

const EmptyTrailersByScacWidget: React.FC<EmptyTrailersByScacWidgetProps> = ({
  selectedFacility,
  compactMode,
  data: dashboardData,
  isRefreshing = false,
  onRefresh
}) => {
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);

  // Process raw data from consolidated query
  const trailers: Trailer[] = useMemo(() => {
    return dashboardData?.trailersInFacility || [];
  }, [dashboardData?.trailersInFacility]);

  const loading = !dashboardData && !error;

  const scacCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    trailers.forEach(t => {
      if (t.status?.name?.toUpperCase() === 'EMPTY' && t.carrier?.name) {
        const scac = t.carrier.name;
        counts[scac] = (counts[scac] || 0) + 1;
      }
    });
    return Object.entries(counts)
      .map(([scac, count]) => ({ scac, count }))
      .sort((a, b) => b.count - a.count);
  }, [trailers]);

  const totalEmpty = useMemo(() => scacCounts.reduce((sum, s) => sum + s.count, 0), [scacCounts]);

  const handleRowClick = (scac: string) => {
    navigate(`/trailers?q=${encodeURIComponent(`EMPTY ${scac}`)}`);
  };

  if (!selectedFacility) {
    return (
      <Paper sx={{ p: 2, height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Typography color="text.secondary">Select a facility</Typography>
      </Paper>
    );
  }

  return (
    <Paper sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <LocalShipping fontSize="small" color="action" />
          <Typography variant={compactMode ? 'body1' : 'h6'} fontWeight="bold">
            Empty Trailers by SCAC
          </Typography>
        </Box>
        {!loading && (
          <Typography variant="body2" color="text.secondary">
            {totalEmpty} total
          </Typography>
        )}
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1 }}>
          <CircularProgress size={24} />
        </Box>
      ) : error ? (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1 }}>
          <Typography color="error" variant="body2">{error}</Typography>
        </Box>
      ) : scacCounts.length === 0 ? (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1 }}>
          <Typography color="text.secondary" variant="body2">No empty trailers</Typography>
        </Box>
      ) : (
        <TableContainer sx={{ flex: 1, overflow: 'auto' }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold', py: 0.5 }}>SCAC</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold', py: 0.5 }}>Empty</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {scacCounts.map(({ scac, count }) => (
                <TableRow
                  key={scac}
                  hover
                  onClick={() => handleRowClick(scac)}
                  sx={{ cursor: 'pointer', '&:last-child td': { border: 0 }, transition: 'opacity 0.3s ease, background-color 0.3s ease' }}
                >
                  <TableCell sx={{ py: 0.5 }}>{scac}</TableCell>
                  <TableCell align="right" sx={{ py: 0.5 }}>{count}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Paper>
  );
};

export default EmptyTrailersByScacWidget;
